import {
  Home,
  FileText,
  Users,
  Building2,
  DollarSign,
  Calendar,
  ChevronRight,
  UserCheck,
  FolderOpen,
  User,
  Briefcase,
  ClipboardList,
  Wrench,
  StickyNote,
  Sparkles,
  CalendarDays,
} from "lucide-react";
import React, { useState } from "react";
import {
  SidebarContent,
  Sidebar,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "../ui/sidebar";
import { NavUser } from "./nav-user";
import { cn } from "../../lib/utils";
import { useAuth } from "../../contexts/authContext";

// Admin menu items
const admin_menu_items = [
  {
    title: "Dashboard",
    url: "/",
    icon: Home,
  },
  {
    title: "Employees Data",
    icon: UserCheck,
    isCollapsible: true,
    items: [
      {
        title: "Employees",
        url: "/employees",
        icon: Users,
      },
      {
        title: "Salary",
        url: "/salary",
        icon: DollarSign,
      },
      {
        title: "Attendance",
        url: "/attendance",
        icon: Calendar,
      },
      {
        title: "Payslips",
        url: "/payslips",
        icon: FileText,
      },
    ],
  },
  {
    title: "Leaves",
    icon: Calendar,
    isCollapsible: true,
    items: [
      {
        title: "Leave Requests",
        url: "/leaves",
        icon: Calendar,
      },
      {
        title: "Comp-Off Requests",
        url: "/compoff",
        icon: ClipboardList,
      },
      {
        title: "Public Holidays",
        url: "/holidays",
        icon: CalendarDays,
      },
    ],
  },
  {
    title: "Notes",
    url: "/notes",
    icon: StickyNote,
  },
  {
    title: "Client Management",
    icon: Building2,
    isCollapsible: true,
    items: [
      {
        title: "Clients",
        url: "/clients",
        icon: Building2,
      },
      {
        title: "Projects",
        url: "/projects",
        icon: FolderOpen,
      },
    ],
  },
];

// Updated Employee menu items with multi-level structure
const employee_menu_items = [
  {
    title: "Dashboard",
    url: "/employee/dashboard",
    icon: Home,
  },
  {
    title: "Attendance & Leaves",
    icon: ClipboardList,
    isCollapsible: true,
    items: [
      {
        title: "Attendance",
        url: "/employee/attendance",
        icon: Calendar,
      },
      {
        title: "Apply Leave",
        url: "/employee/leaves",
        icon: Briefcase,
      },
      {
        title: "Comp-Offs",
        url: "/employee/compoff",
        icon: Sparkles,
      },
      {
        title: "Public Holidays",
        url: "/employee/holidays",
        icon: CalendarDays,
      },
    ],
  },
  {
    title: "Utilities",
    icon: Wrench,
    isCollapsible: true,
    items: [
      {
        title: "Notes",
        url: "/employee/notes",
        icon: StickyNote,
      },
    ],
  },
  {
    title: "Financial",
    icon: DollarSign,
    isCollapsible: true,
    items: [
      {
        title: "Salary Details",
        url: "/employee/salary",
        icon: DollarSign,
      },
      {
        title: "Payslips",
        url: "/employee/payslips",
        icon: FileText,
      },
    ],
  },
  {
    title: "My Information",
    icon: User,
    isCollapsible: true,
    items: [
      {
        title: "Documents",
        url: "/employee/documents",
        icon: FolderOpen,
      },
      {
        title: "Team Members",
        url: "/employee/team-members",
        icon: Users,
      },
    ],
  },
];

interface CollapsibleMenuItemProps {
  item: {
    title: string;
    url?: string;
    icon?: React.ComponentType<any>;
    isCollapsible?: boolean;
    items?: Array<{
      title: string;
      url: string;
      icon?: React.ComponentType<any>;
    }>;
  };
}

function CollapsibleMenuItem({ item }: CollapsibleMenuItemProps) {
  const [isOpen, setIsOpen] = useState(true); // Default open for better UX

  if (!item.isCollapsible) {
    // Regular menu item
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild>
          <a href={item.url}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  // Collapsible menu item
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setIsOpen(!isOpen)}
        className="group/collapsible"
        data-state={isOpen ? "open" : "closed"}
      >
        {item.icon && <item.icon />}
        <span>{item.title}</span>
        <ChevronRight
          className={cn(
            "ml-auto transition-transform duration-200",
            isOpen && "rotate-90"
          )}
        />
      </SidebarMenuButton>
      {isOpen && item.items && (
        <SidebarMenuSub>
          {item.items.map((subItem) => (
            <SidebarMenuSubItem key={subItem.title}>
              <SidebarMenuSubButton asChild>
                <a href={subItem.url}>
                  {subItem.icon && <subItem.icon />}
                  <span>{subItem.title}</span>
                </a>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

export default function AppSidebar() {
  const { user } = useAuth();

  // Determine which menu items to show based on role
  const menu_items =
    user?.role === "employee" ? employee_menu_items : admin_menu_items;

  // Determine header title based on role
  const headerTitle =
    user?.role === "employee" ? "Employee Portal" : "Payslip Management";
  const headerSubtitle =
    user?.role === "employee" ? "Self Service" : "Enterprise";

  const data = {
    user: {
      name: user?.fullName || user?.email?.split("@")[0] || "User",
      email: user?.email || "user@company.com",
      role: user?.role || "admin",
    },
  };

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <a href={user?.role === "employee" ? "/employee/dashboard" : "/"}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {user?.role === "employee" ? (
                    <User className="size-4" />
                  ) : (
                    <Building2 className="size-4" />
                  )}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{headerTitle}</span>
                  <span className="truncate text-xs">{headerSubtitle}</span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>
            {user?.role === "employee" ? "Employee Menu" : "Platform"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menu_items.map((item) => (
                <CollapsibleMenuItem key={item.title} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
    </Sidebar>
  );
}
